/**
 * Utility for handling wallet transaction errors consistently across the app
 */

import { decodeContractError, formatContractError, type DecodedContractError } from './contractRegistry';

interface WalletError {
  code?: number | string;
  message?: string;
  reason?: string;
  shortMessage?: string;
}

/**
 * Check if an error is a user rejection (user canceled/rejected the transaction in their wallet)
 */
export function isUserRejection(error: unknown): boolean {
  if (!error) return false;

  const err = error as WalletError;

  // EIP-1193 user rejection code
  if (err.code === 4001) return true;

  // ethers v6 action rejected code
  if (err.code === "ACTION_REJECTED") return true;

  // Check message patterns
  const message = (err.message || err.reason || err.shortMessage || "").toLowerCase();

  const rejectionPatterns = [
    "user rejected",
    "user denied",
    "user canceled",
    "user cancelled",
    "rejected the request",
    "rejected by user",
    "transaction was rejected",
    "request rejected",
  ];

  return rejectionPatterns.some(pattern => message.includes(pattern));
}

/**
 * Get a user-friendly error message for wallet errors
 * Returns null for user rejections (so they can be handled differently)
 */
export function getWalletErrorMessage(error: unknown, fallback: string = "Transaction failed"): string | null {
  if (isUserRejection(error)) {
    return null; // Return null for user rejections - caller should handle differently
  }

  if (error instanceof Error) {
    // Try to extract a cleaner message
    const err = error as WalletError;

    // ethers v6 often has a shortMessage that's cleaner
    if (err.shortMessage) {
      return err.shortMessage;
    }

    // Use reason if available (often from contract reverts)
    if (err.reason) {
      return err.reason;
    }

    // Fall back to message, but truncate if too long
    if (err.message) {
      const msg = err.message;
      // Truncate very long error messages
      if (msg.length > 100) {
        return msg.slice(0, 100) + "...";
      }
      return msg;
    }
  }

  return fallback;
}

/**
 * Short message for user rejection toast
 */
export const USER_REJECTED_MESSAGE = "Transaction cancelled";

/**
 * Log a contract error with decoded information for debugging
 * This provides detailed information about which contract/function failed
 */
export function logContractError(context: string, error: unknown): DecodedContractError | null {
  // Skip logging for user rejections
  if (isUserRejection(error)) {
    console.log(`[${context}] User rejected transaction`);
    return null;
  }

  console.error(`[${context}] Error:`, error);

  const decoded = decodeContractError(error);
  if (decoded) {
    console.error(`[${context}] Contract error details:\n${formatContractError(decoded)}`);
  }

  return decoded;
}

export type { DecodedContractError };
